// articles/StrokePlayGame.js
// Game guide (Oct 4, 2026), in the Nassau guide's format: Stroke Play for a weekly round
// (Dennis: groups like Kirwin's play it), with the event steps left to the Stroke Play event
// guide. Settings and labels follow that guide and App.js StrokePlayGamePanel; change them
// here if they change there.
import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-stroke-play-game');

const FAQS = [
  {
    q: 'Can I rank net and gross at the same time?',
    a: 'Yes. Choose Both. TTO ranks a net and a gross competition from the same cards, and a player can place in both. With a buy-in, you say how much of each buy-in goes to the gross purse; the rest goes to net.',
  },
  {
    q: 'What handicap does each player get?',
    a: 'His playing handicap: the allowance (95% by default) applied to his course handicap from his own tee, rounded once. His strokes fall on the hardest holes of his own tee. Many groups set the allowance to 100%.',
  },
  {
    q: 'Why were more players paid than places?',
    a: 'Because of a tie and the Tied scores share the money choice: players on the same score split the money for the places they occupy. Choose Pay by the decided order to pay exactly your places.',
  },
  {
    q: 'Can I pay the front nine and back nine separately?',
    a: 'Yes. In What it pays, choose Front & back, or Front, back & overall. Each part has its own share of the buy-in and its own places. It needs an 18-hole round ranked on Net or Gross.',
  },
  {
    q: 'Can we play it beside other games?',
    a: 'Yes. Stroke Play runs off the same cards as skins, Nassau, Dots and the other games. A round has one Stroke Play game.',
  },
  {
    q: 'Do Stroke Play rounds post to GHIN?',
    a: 'Yes. Every player plays his own ball, so the round is acceptable for handicap purposes. Players post from the app.',
  },
];

export default function StrokePlayGame() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up a Stroke Play Game
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        Everyone plays his own ball and the lowest total wins, ranked on net, gross or both. Each
        player puts in a buy-in, and TTO pays the places you choose: winner takes all, or first and
        second, for example. You can also pay the front nine, the back nine and the 18 as separate
        competitions.
      </P>

      <StepTracker
        storageKey="tto_guide_stroke_game_steps"
        steps={['Add Stroke Play', 'Who\u2019s in', 'Play & follow the standings', 'Results & settle']}
        note="Steps 1 and 2 happen before the round; 3 and 4 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Stroke Play needs live scoring.</strong> Every player in it needs his scores entered in
          the app as he plays, on his own phone or by the player keeping his group&rsquo;s card.
        </LI>
        <LI>
          <strong>Stroke Play isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          Decide with your group: net, gross or both; the handicap allowance; the buy-in; how many
          places you pay; and whether the front, back and 18 pay separately.
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Add Stroke Play" when="Before the round">
        <Heads>
          <strong>On a net game, every player needs a complete profile.</strong> Each
          player&rsquo;s profile must include a <strong>preferred tee</strong> and a{' '}
          <strong>Handicap Index</strong>, so that his course handicap can be worked out. Check them
          in Admin &rarr; Players.
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Stroke Play</strong>.
          </LI>
          <LI>
            <strong>Rank on:</strong> <Btn>Net</Btn>, <Btn>Gross</Btn> or <Btn>Both</Btn>. With Both,
            TTO ranks a net and a gross competition from the same cards.
          </LI>
          <LI>
            <strong>Handicap allowance:</strong> 95% by default, the USGA recommendation. Many groups
            play 100%. It&rsquo;s applied to each player&rsquo;s course handicap and rounded once;{' '}
            <strong>Reset to 95</strong> puts it back.
          </LI>
          <LI>
            <strong>Payouts:</strong>
            <UL>
              <LI>
                <strong>Winner takes all:</strong> enter the <strong>Buy-in</strong> per player; every
                buy-in goes to first place, split on a tie.
              </LI>
              <LI>
                <strong>Paid places:</strong> enter the <strong>Buy-in</strong>, choose{' '}
                <strong>Places paid</strong>, and set each place&rsquo;s percentage (for example
                60 / 40). They must add up to 100%.
              </LI>
              <LI>
                <strong>Handled outside TTO:</strong> TTO names the places and you settle the money
                yourself. Set <strong>Places decided</strong> to the number of places you award.
              </LI>
            </UL>
            TTO pays out the whole buy-in; there&rsquo;s no fee kept back.
          </LI>
          <LI>
            <strong>What it pays:</strong> <Btn>Whole round</Btn>, <Btn>Front &amp; back</Btn> or{' '}
            <Btn>Front, back &amp; overall</Btn>. With the nines paid separately, each part is its own
            competition with its own share of the buy-in and its own places, and the standings get
            Front 9 / Back 9 / Overall tabs. It needs an 18-hole round ranked on Net or Gross (not
            Both).
          </LI>
          <LI>
            <strong>Money on a tie:</strong> <strong>Tied scores share the money</strong> (tied
            players split the money for the places they occupy), or <strong>Pay by the decided
            order</strong> (the tie-break below decides the places).
          </LI>
          <LI>
            <strong>How ties are decided:</strong> <strong>Scorecard playoff (USGA)</strong> (best
            score over the last 9, then 6, then 3, then the 18th), <strong>Hole handicap</strong>{' '}
            (hardest hole first), or <strong>We&rsquo;ll decide</strong> (you put them in order).
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Choose who's in" when="After you save, until the first score">
        <UL>
          <LI>
            A new Stroke Play game starts with nobody in it. After you save, the game shows a{' '}
            <strong>Player Opt-Ins</strong> list with every player on the tee time.
          </LI>
          <LI>
            Click a player&rsquo;s <strong>— Out</strong> to make it <strong>✓ In</strong>, or the
            reverse.
          </LI>
          <LI>
            Players can also tap <Btn>Opt In</Btn> in their own app, until the first score of the
            round is entered. After that only you can add or remove a player.
          </LI>
          <LI>
            Only players who are in are ranked and pay the buy-in.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={3} title="Play and follow the standings" when="During the round">
        <UL>
          <LI>
            Each player enters his scores hole by hole, or one player keeps the card for his group
            with <strong>+ Score for another player</strong>.
          </LI>
          <LI>
            <strong>Picked up?</strong> Tap <strong>X</strong>. It counts as a net double bogey and
            shows as &ldquo;7x&rdquo;.
          </LI>
          <LI>
            Players follow the standings in their app: <strong>🏆 Leaderboard</strong> &rarr;{' '}
            <strong>💰 Games</strong>, with each player&rsquo;s place, score and holes played.
          </LI>
          <LI>
            With a buy-in, places show during the round; the dollar amounts wait until every card is
            complete.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="Results and settling up" when="When the last card is in">
        <UL>
          <LI>
            The round finalizes itself once every card is complete. You can also click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>. If a card is
            incomplete, TTO lists the player and lets you mark him DNF or wait for his scores.
          </LI>
          <LI>
            <strong>Ties for a paid place</strong> are broken by the method you chose. Identical
            cards, or <strong>We&rsquo;ll decide</strong>, need your call: in <Btn>🎮 Games</Btn>, put
            the tied players in order with ▲ ▼ and click <Btn>Use this order</Btn>.
          </LI>

          <LI>
            With a buy-in, TTO settles the money player to player, every player gets a statement with
            the placings and his net scorecard, and you can email everyone their results with one
            click.
          </LI>
          <LI>
            <strong>A score needs fixing after finalize?</strong> Click <Btn>↶ Reopen Round</Btn> in{' '}
            <Btn>🎮 Games</Btn>, correct it in Admin &rarr; <strong>⛳ Scoring</strong> &rarr;{' '}
            <strong>⛳ Post-Round Score Entry</strong>, then finalize again.
          </LI>
        </UL>
      </Step>

      <H2>Running Stroke Play as an event?</H2>
      <P>
        For a tournament (registration, starting holes, printed scorecards and the clubhouse
        leaderboard), use the event guide instead: 📖 Guides &rarr; Setting up Events / Tournaments
        &rarr; <strong>Stroke Play</strong>. The game settings are the same as step 1 here.
      </P>
      <H2>What changes once scoring starts</H2>
      <UL>
        <LI>
          <strong>Players can no longer opt in or out themselves</strong> once the first score of the
          round is entered. You still can, from <Btn>🎮 Games</Btn>.
        </LI>
        <LI>
          <strong>Settings can still change</strong>, but change them only if you must: the standings
          and any money follow the new settings.
        </LI>
      </UL>

      <FAQ id="stroke-game" faqs={FAQS} />

      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
